import {
  Box,
  Button,
  Card,
  CardContent,
  Link as MuiLink,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import type { DocStatus } from "@xop/dashboard-shared";

import { listCategories } from "./api.js";
import { folderOptions } from "./folderOptions.js";
import { FolderSelect } from "./FolderSelect.js";
import { applyBulkPatch, runBulkUpload, type BulkFileState } from "./bulkUpload.js";
import { BulkFileList } from "./bulkUploadUi.js";
import { BackButton } from "@/components/ui/back-button";

/** Bulk drag-and-drop upload: drop a folder's worth of files, pick the target
 * category, and each file becomes its own Document under that category. Reuses
 * the single-file upload endpoint once per file (sequential). Built so the full
 * xopnetworks.com catalogue can be loaded a folder at a time. */
export function DocumentBulkUploadPage(): JSX.Element {
  const qc = useQueryClient();

  const [items, setItems] = useState<BulkFileState[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState<DocStatus>("published");
  const [categoryId, setCategoryId] = useState("");
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState<{ done: number; failed: number } | null>(null);

  const { data: cats } = useQuery({ queryKey: ["docs", "categories"], queryFn: listCategories });
  const folders = useMemo(() => folderOptions(cats?.data ?? []), [cats]);
  const categoryName = useMemo(
    () => folders.find((f) => String(f.id) === categoryId)?.label ?? null,
    [folders, categoryId],
  );

  const addFiles = (picked: FileList | null): void => {
    if (!picked || picked.length === 0) return;
    const incoming = Array.from(picked);
    setFiles((prev) => {
      // De-dupe by name+size so re-dropping the same folder doesn't double up.
      const key = (f: File): string => `${f.name}:${f.size}`;
      const seen = new Set(prev.map(key));
      return [...prev, ...incoming.filter((f) => !seen.has(key(f)))];
    });
    setItems((prev) => {
      const seen = new Set(prev.map((s) => `${s.name}:${s.size}`));
      const fresh = incoming
        .filter((f) => !seen.has(`${f.name}:${f.size}`))
        .map<BulkFileState>((f) => ({ name: f.name, size: f.size, status: "pending" }));
      return [...prev, ...fresh];
    });
    setSummary(null);
  };

  const reset = (): void => {
    setFiles([]);
    setItems([]);
    setSummary(null);
  };

  const start = async (): Promise<void> => {
    if (files.length === 0 || running) return;
    setRunning(true);
    setSummary(null);
    // Snapshot which files are already uploaded so a re-run skips them (no dupes).
    const alreadyDone = items.map((s) => s.status === "done");
    const res = await runBulkUpload(
      files,
      { status, ...(categoryId ? { categoryId } : {}) },
      (i, patch) => setItems((prev) => prev.map((s, idx) => (idx === i ? applyBulkPatch(s, patch) : s))),
      undefined,
      (i) => alreadyDone[i] === true,
    );
    setRunning(false);
    setSummary(res);
    void qc.invalidateQueries({ queryKey: ["docs"] });
  };

  const doneCount = items.filter((s) => s.status === "done").length;

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <Box component="header">
        <BackButton to="/documentation/manage/new" label="Back" sx={{ mb: 1.5 }} />
        <Typography component="h2" sx={{ mt: 0.5, fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
          Bulk upload documents
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>
          Pick a category, then drop a whole folder of files. Each file becomes
          its own document in that category. Files land in DigitalOcean Spaces and
          the AI gateway indexes them.{" "}
          <MuiLink component={RouterLink} to="/documentation/manage/bulk-delete" underline="hover">
            Bulk delete →
          </MuiLink>
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ display: "grid", gap: 2.5 }}>
          {/* Target category + metadata — chosen BEFORE dropping so the whole
              batch lands in the right folder. */}
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { md: "repeat(2, 1fr)" } }}>
            <FolderSelect
              folders={folders}
              value={categoryId}
              onChange={setCategoryId}
              label="Folder"
              emptyLabel="Uncategorized"
              helperText="Every dropped file goes into this folder or sub-folder"
            />
            <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value as DocStatus)} fullWidth>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="in_review">In review</MenuItem>
              <MenuItem value="published">Published</MenuItem>
            </TextField>
          </Box>

          <Box
            data-testid="bulk-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              addFiles(e.dataTransfer.files);
            }}
            sx={[
              { borderRadius: 1, border: 2, borderStyle: "dashed", p: 4, textAlign: "center", transition: "background-color 150ms, border-color 150ms" },
              dragOver ? { borderColor: "primary.main", bgcolor: "action.selected" } : { borderColor: "divider" },
            ]}
          >
            <Typography sx={{ fontSize: "1.5rem", color: "text.disabled" }}>↑</Typography>
            <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 500 }}>
              Drag files here{categoryName ? ` → ${categoryName}` : ""}
            </Typography>
            <Typography sx={{ mb: 1.5, fontSize: 12, color: "text.disabled" }}>
              Drop many at once — one document is created per file.
            </Typography>
            <Box component="label" sx={{ display: "inline-flex" }}>
              <Box
                component="input"
                type="file"
                multiple
                sx={{ display: "none" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => addFiles(e.target.files)}
              />
              <Box
                component="span"
                sx={{ display: "inline-flex", height: 32, cursor: "pointer", alignItems: "center", borderRadius: 1, bgcolor: "surfaceSoft", px: 1.5, fontSize: 12, fontWeight: 500, "&:hover": { filter: "brightness(1.05)" } }}
              >
                Pick files
              </Box>
            </Box>
          </Box>

          <BulkFileList items={items} categoryName={categoryName} running={running} summary={summary} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: 1, borderColor: "divider", pt: 2 }}>
            <Button type="button" variant="text" color="inherit" size="small" onClick={reset} disabled={running || items.length === 0}>
              Clear
            </Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button component={RouterLink} to="/documentation" variant="text" color="inherit" size="small">
                Done
              </Button>
              <Button type="button" variant="contained" size="small" onClick={() => void start()} disabled={files.length === 0 || running}>
                {running ? `Uploading… (${doneCount}/${items.length})` : `Upload ${items.length || ""} file${items.length === 1 ? "" : "s"}`}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
